import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Mail, Phone, MapPin, Send } from "lucide-react";
import { api, LOGO_DARK } from "../../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("Subscribed. Welcome to the movement.");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#0B1526] text-white relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#C00000] blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#1F3864] blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        <div className="border-b border-white/10 pb-14 mb-14">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <div>
              <div className="text-[11px] tracking-[0.3em] text-[#C00000] uppercase mb-4">
                Our Promise
              </div>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
                Praise
                <br />
                <span className="text-[#C00000]">Beyond</span> Limits.
              </h2>
            </div>

            <form onSubmit={subscribe} className="flex-shrink-0 w-full lg:w-[420px]" data-testid="form-newsletter">
              <label className="block text-xs tracking-[0.2em] uppercase text-white/60 mb-3">
                Join our newsletter
              </label>
              <div className="flex border border-white/20">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-white/40 focus:outline-none"
                  data-testid="input-newsletter-email"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="px-5 bg-[#C00000] hover:bg-[#990000] transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-60"
                  data-testid="action-newsletter-submit-button"
                >
                  <Send className="w-4 h-4" />
                  {busy ? "..." : "Subscribe"}
                </button>
              </div>
              <p className="mt-2 text-xs text-white/40">
                We send one email a month. No spam. Unsubscribe anytime.
              </p>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={LOGO_DARK} alt="Next Gen Praise" className="h-14 w-14 object-contain bg-white/5 p-1 rounded-sm" />
              <div>
                <div className="font-display text-xl">Next Gen Praise NPC</div>
                <div className="text-[10px] tracking-[0.2em] text-[#C00000] uppercase">Praise Beyond Limits</div>
              </div>
            </Link>
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              A gospel music platform raising the next generation of anointed worshippers across
              South Africa and beyond.
            </p>
            <div className="flex gap-3 mt-5">
              <a href="#" className="w-9 h-9 border border-white/20 hover:bg-[#C00000] hover:border-[#C00000] flex items-center justify-center transition-colors" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
              <a href="#" className="w-9 h-9 border border-white/20 hover:bg-[#C00000] hover:border-[#C00000] flex items-center justify-center transition-colors" aria-label="YouTube"><Youtube className="w-4 h-4" /></a>
              <a href="#" className="w-9 h-9 border border-white/20 hover:bg-[#C00000] hover:border-[#C00000] flex items-center justify-center transition-colors" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
            </div>
          </div>

          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-4">Explore</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="text-white/80 hover:text-white">About Us</Link></li>
              <li><Link to="/artists" className="text-white/80 hover:text-white">Artists</Link></li>
              <li><Link to="/videos" className="text-white/80 hover:text-white">Videos</Link></li>
              <li><Link to="/events" className="text-white/80 hover:text-white">Events</Link></li>
              <li><Link to="/shop" className="text-white/80 hover:text-white">Shop</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-4">Engage</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/membership" className="text-white/80 hover:text-white">Membership</Link></li>
              <li><Link to="/artist-development" className="text-white/80 hover:text-white">Development</Link></li>
              <li><Link to="/donate" className="text-white/80 hover:text-white">Donate</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-white">Contact</Link></li>
              <li><Link to="/login" className="text-white/80 hover:text-white">Member Login</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-4">Reach Us</div>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#C00000]" /><span>189 Prince Street,<br />Durban, KZN, 4001</span></li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#C00000]" /><a href="mailto:admin@nextgenpraise.com" className="hover:text-white">admin@nextgenpraise.com</a></li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#C00000]" /><a href="tel:+27735465752" className="hover:text-white">+27 73 546 5752</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Next Gen Praise NPC. All rights reserved. Reg: 2026/237461/08</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">POPIA</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
